import React from 'react';
import { Link } from 'react-router-dom';

const CustomAI: React.FC = () => {
    return (
        <div>
            {/* Hero Section */}
            <section className="bg-primary py-20">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-inter">
                            Custom AI Projects
                        </h1>
                        <p className="text-xl text-gray-100 mb-8 max-w-3xl mx-auto">
                            Tailored artificial intelligence solutions to solve your unique business challenges
                        </p>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 bg-white">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-primary mb-6">Why Choose Custom AI?</h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Off-the-shelf solutions often fall short of addressing specific organizational needs. Our custom AI development ensures you get a solution that fits your workflow perfectly.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                        <div className="group relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-primary/20 transform hover:-translate-y-2 overflow-hidden flex flex-col h-full">
                            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            <div className="relative z-10">
                                <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-105 shadow-lg shadow-primary/20">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-primary transition-colors duration-300">Predictive Analytics</h3>
                                <p className="text-gray-600">Leverage your data to forecast trends and make informed decisions with high-precision models.</p>
                            </div>
                        </div>
                        <div className="group relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-primary/20 transform hover:-translate-y-2 overflow-hidden flex flex-col h-full">
                            <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            <div className="relative z-10">
                                <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-105 shadow-lg shadow-secondary/20">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-secondary transition-colors duration-300">Computer Vision</h3>
                                <p className="text-gray-600">Automate visual inspection, facial recognition, and object detection with state-of-the-art accuracy.</p>
                            </div>
                        </div>
                        <div className="group relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-primary/20 transform hover:-translate-y-2 overflow-hidden flex flex-col h-full">
                            <div className="absolute inset-0 bg-gradient-to-br from-dark/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            <div className="relative z-10">
                                <div className="w-16 h-16 bg-dark rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-105 shadow-lg shadow-dark/20">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-dark transition-colors duration-300">NLP Solutions</h3>
                                <p className="text-gray-600">Advanced text analysis, sentiment analysis, and content generation for specialized domains.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-14 bg-white border-t border-gray-100 mt-10">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#0B1538] mb-4">
                        Have a Specific AI Project in Mind?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Let's discuss your requirements and build a solution that drives value.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/contact"
                            className="px-8 py-4 bg-[#0B25E6] text-white rounded-lg hover:bg-blue-800  transition-colors text-lg font-semibold"
                        >
                            Start Your Project
                        </Link>
                        <Link
                            to="/contact"
                            className="px-8 py-4 text-white border-2 border-white rounded-lg hover:bg-blue-800 text-white transition-colors text-lg font-semibold"
                        >
                            Consult with Experts
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CustomAI;
